import { useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useEffectEvent } from "@pexip-engage/utils";
import {
  ActionFunctionArgs,
  json,
  type LoaderFunctionArgs,
  redirect,
  type SerializeFrom,
} from "@remix-run/node";
import { useLoaderData } from "@remix-run/react";

import { AppErrorBoundary } from "~/components/AppErrorBoundary";
import { OfficeIcon } from "~/components/Icons/OfficeIcon";
import { LocationSearch } from "~/components/LocationSearch";
import { StepInfo } from "~/components/Step/Step";
import { Text } from "~/components/Text";
import { handleOfficeAction } from "~/modules/office/office.action.server";
import { handleOfficeLoader } from "~/modules/office/office.loader.server";
import { OfficeSelector } from "~/modules/office/OfficeSelector";
import { useStateManager } from "~/modules/StateManager.hooks";
import { createServerStateManager } from "~/modules/StateManager.server";
import { useOfficeApiFetcher } from "~/routes/api+/enterprises.$enterprise.offices";
import type { PlaceDetailLoaderData } from "~/routes/api+/enterprises.$enterprise.place-details";
import { requireStep } from "~/utils/validators.utils.server";

export interface OfficeLoaderData extends SerializeFrom<typeof loader> {}

export async function action(args: ActionFunctionArgs) {
  return args.context.measurer.time(
    "$enterprise_schedule_office_action",
    async function scheduleOfficeAction() {
      const stateManager = await createServerStateManager(args);
      const { nextStep } = stateManager.getStepConfig("office");
      requireStep(nextStep, "Office");

      await handleOfficeAction(stateManager);

      return redirect(`../${nextStep.pathname}`);
    },
  );
}

export async function loader(args: LoaderFunctionArgs) {
  return args.context.measurer.time(
    "$enterprise_schedule_office",
    async function scheduleOfficeLoader() {
      const stateManager = await createServerStateManager(args);
      const { nextStep } = stateManager.getStepConfig("office");
      requireStep(nextStep, "Office");

      const result = await handleOfficeLoader(stateManager);

      if (result.type === "redirect") {
        return redirect(`../${nextStep.pathname}`);
      }

      return json(result.payload);
    },
  );
}

export default function OfficeRoute() {
  const { t } = useTranslation();
  const {
    data: { regularOffices, virtualOffices },
    initialLocation,
    loadOffices,
    searchedGeolocation,
  } = useOfficeFetcher();
  const stateManager = useStateManager();
  const officeSearchRequired =
    stateManager.state.config.offices.ids.length === 0 &&
    stateManager.state.config.application.officeSearch === "required";

  return (
    <div className="flex flex-col" data-component="OfficeStep">
      <StepInfo i18nKey="steps.Office.Info" />
      <LocationSearch
        defaultPlaceId={initialLocation.placeId}
        label={t("LocationSearch.label")}
        onSelectionChange={loadOffices}
      />
      {officeSearchRequired && !searchedGeolocation ? (
        <div className="flex flex-col items-center justify-center gap-sm py-sm text-center text-gray-500 md:py-xl">
          <div className="h-[28px] w-[28px]">
            <OfficeIcon outline />
          </div>
          <Text i18nKey="steps.Office.search_required_empty_state" />
        </div>
      ) : (
        <div className="flex w-full flex-col" data-component="OfficesContainer">
          {virtualOffices.length !== 0 && (
            <>
              <span className="py-sm text-gray-500">
                <Text i18nKey="steps.Office.Subtitle.virtual_office" />
              </span>
              <Offices offices={virtualOffices} />
            </>
          )}

          {regularOffices.length !== 0 && (
            <span className="py-sm text-gray-500">
              <Text i18nKey="steps.Office.Subtitle.local_office" />
            </span>
          )}
          <Offices offices={regularOffices} />
        </div>
      )}
    </div>
  );
}

function useOfficeFetcher() {
  const loaderData = useLoaderData<typeof loader>();

  const fetcher = useOfficeApiFetcher();
  const load = useEffectEvent(fetcher.submit);

  const loadOffices = useCallback(
    (location: PlaceDetailLoaderData) => {
      const searchParams = new URLSearchParams();
      location?.geolocation && searchParams.set("nearestToGeo", location?.geolocation);

      load(searchParams.toString());
    },
    [load],
  );

  const data = useMemo(() => {
    const offices = fetcher.data?.offices ?? loaderData.offices;

    const virtualOffices: typeof offices = [];
    const regularOffices: typeof offices = [];

    for (const office of offices) {
      if (office.isVirtual) {
        virtualOffices.push(office);
      } else {
        regularOffices.push(office);
      }
    }

    return { virtualOffices, regularOffices };
  }, [fetcher.data?.offices, loaderData.offices]);

  return {
    ...fetcher,
    initialLocation: loaderData.location,
    data,
    searchedGeolocation: Boolean(fetcher.data?.sort.nearestToGeo ?? loaderData.sort.nearestToGeo),
    loadOffices,
  };
}

interface OfficesProps {
  offices?: OfficeLoaderData["offices"];
}

function Offices({ offices }: OfficesProps) {
  return (
    <div className="flex flex-col gap-sm">
      {offices?.map((office) => <OfficeSelector key={office.id} office={office} />)}
    </div>
  );
}

export function ErrorBoundary() {
  return <AppErrorBoundary />;
}
